import React, { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import ClassPage from "./ClassPage";
import CategoryPage from "./CategoryPage";
import SearchClasses from "../../pages/searchResult/SearchClasses";
import Button from "../components/Button";
import { resolvePermalink } from "../api/redesignApi";
import { paths } from "../routing/paths";

/*
 * The archive permalink resolver — every class/category URL lands here via
 * the router's catch-all. Resolves once through user/url-base:
 *   class     → redesigned ClassPage
 *   category  → redesigned CategoryPage (the browse/list surface)
 *   not found → designed 404 (never blank)
 *   anything else (unknown future types) → legacy rendering as a safety net
 */

export function DesignedNotFound({ node }) {
  return (
    <PublicLayout>
      <div className="max-w-[640px] w-full mx-auto px-5 py-16 flex flex-col items-center gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-ic-lightGray flex items-center justify-center">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#515F7B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
        </div>
        <h1 className="m-0 text-[28px] font-bold leading-9 tracking-[-0.3px] text-black">
          We couldn't find that page
        </h1>
        <p className="m-0 text-[18px] leading-7 text-ic-body">
          The address <span className="font-mono text-[15px] ic-wrap-anywhere">/{node}</span> doesn't match a class or
          category in the archive. With 15,000+ classes, what you're looking for almost certainly
          exists — search will find it.
        </p>
        <div className="flex gap-3 flex-wrap justify-center mt-2">
          <Link to={paths.search()}>
            <Button size="lg">Search the archive</Button>
          </Link>
          <Link
            to={paths.home()}
            className="inline-flex items-center h-12 px-5 text-[16px] font-semibold text-ic-body border border-ic-borderGray rounded-3xl no-underline transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue hover:no-underline"
          >
            Back to the homepage
          </Link>
        </div>
      </div>
    </PublicLayout>
  );
}

function LoadingState() {
  return (
    <PublicLayout>
      <div className="max-w-[1280px] w-full mx-auto px-5 pt-8 flex flex-col gap-4" aria-busy="true" aria-label="Loading">
        <div className="h-4 w-48 rounded bg-ic-lightGray animate-pulse" />
        <div className="h-10 w-3/4 rounded bg-ic-lightGray animate-pulse" />
        <div className="h-4 w-2/3 rounded bg-ic-lightGray animate-pulse" />
        <div className="grid gap-5 mt-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))" }}>
          <div className="h-72 rounded-xl bg-ic-navy/90 animate-pulse" />
          <div className="h-72 rounded-xl bg-ic-lightGray animate-pulse" />
        </div>
      </div>
    </PublicLayout>
  );
}

export default function PermalinkPage() {
  const location = useLocation();
  const node = location.pathname.replace(/\/+$/, "").replace(/^\/+/, "");
  const [state, setState] = useState({ status: "loading", result: null });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", result: null });
    resolvePermalink(node)
      .then((result) => !cancelled && setState({ status: "done", result }))
      .catch(() => !cancelled && setState({ status: "done", result: { kind: "error" } }));
    return () => {
      cancelled = true;
    };
  }, [node]);

  if (state.status === "loading") return <LoadingState />;

  const result = state.result;
  if (result?.kind === "class") return <ClassPage classData={result} />;
  if (result?.kind === "category") {
    // "Classic view" escape hatch (category-page reference, top-right link):
    // the pre-redesign file-directory rendering of the same node.
    if (new URLSearchParams(location.search).get("view") === "classic") {
      return <SearchClasses uniqueName={node} />;
    }
    return <CategoryPage category={result} />;
  }
  if (result?.kind === "not-found" || result?.kind === "error") return <DesignedNotFound node={node} />;

  // Unknown result type — legacy rendering as a safety net.
  return <SearchClasses uniqueName={node} />;
}
